// NOTICE: KAPTION-INTEGRATION
// Secure Assignment Mode — shared helpers for the admin visibility guardrails
// (group-visibility.MULTI_GROUP.*, guardrails G1/G2 in MULTI-GROUP-PROPOSAL.md §4.3.3).
//
// ─────────────────────────────────────────────────────────────────────────────
// The semantics encoded here are the ones the SERVER actually implements, which
// are not quite the ones the proposal's prose described. Measured in Phase 0
// (spec/services/conversations/kaption_group_visibility_service_spec.rb, the
// MULTI_GROUP.5 example) against unmodified production code:
//
//   Visible(u) = { assigned to u }
//              ∪ ⋃ { every conversation of t } for each restricted t that u LEADS
//              ∪ { open-team conversations in inboxes u belongs to }
//
// The third term is keyed on INBOX membership, not on membership of the open team
// (kaption_group_visibility.rb:91 filters an already-inbox-scoped relation by
// team_id, with no team_members predicate). So:
//
//   - Being a plain member of a restricted group grants nothing beyond your own
//     assigned conversations in it. Roles are per membership row.
//   - Joining an OPEN group does not itself widen anything. A person already sees
//     that group's conversations in any inbox they belong to, whether or not they
//     are in the team.
//
// That is why the warnings below describe EFFECTIVE VISIBILITY and point the admin
// at inbox membership as the real lever — rather than claiming the click they are
// about to make causes the grant, which would be false.
// ─────────────────────────────────────────────────────────────────────────────
import { ref } from 'vue';
import KaptionGroupVisibilityAPI from 'dashboard/api/kaptionGroupVisibility';

export function useKaptionGroupVisibility() {
  const agentGroups = ref({});
  const isLoadingAgentGroups = ref(false);

  /**
   * Loads per-agent group memberships, keyed by user id.
   * Failures are swallowed: every consumer is an advisory overlay, and a broken
   * advisory must never block the admin action it annotates.
   */
  const fetchAgentGroups = async () => {
    try {
      isLoadingAgentGroups.value = true;
      const { data } = await KaptionGroupVisibilityAPI.agentGroupMemberships();
      agentGroups.value = (data?.agents || []).reduce((acc, agent) => {
        acc[agent.user_id] = agent;
        return acc;
      }, {});
    } catch (error) {
      agentGroups.value = {};
    } finally {
      isLoadingAgentGroups.value = false;
    }
  };

  const groupsFor = userId => agentGroups.value[userId] || null;

  const ledGroups = membership =>
    (membership?.restricted || []).filter(group => group.role === 'leader');

  const plainRestrictedGroups = membership =>
    (membership?.restricted || []).filter(group => group.role !== 'leader');

  const openGroups = membership => membership?.open || [];

  const names = groups => (groups || []).map(group => group.name).join(', ');

  /**
   * A compact, translatable descriptor of what this person can actually see.
   * Returns { kind, led, open } — the caller renders the sentence so the phrasing
   * stays in the locale files.
   *
   *   'administrator' / 'coordinator' — the rule is short-circuited entirely
   *   'leader'  — own assigned + everything in the groups they lead (+ open term)
   *   'own'     — own assigned only (+ open term)
   */
  const effectiveVisibility = membership => {
    if (!membership) return { kind: 'own', led: [], open: [] };
    if (membership.is_administrator)
      return { kind: 'administrator', led: [], open: [] };
    if (membership.is_coordinator)
      return { kind: 'coordinator', led: [], open: [] };

    const led = ledGroups(membership);
    return {
      kind: led.length ? 'leader' : 'own',
      led,
      open: openGroups(membership),
    };
  };

  /**
   * group-visibility.GUARDRAIL.1 / group-visibility.GUARDRAIL.2. Given the agents about to be added to `team`,
   * returns one entry
   * per agent whose EFFECTIVE visibility is wider than the team's classification
   * would lead an admin to assume. Both directions (proposal Q8/Q9):
   *
   *   direction 'open'       — adding someone who is in a restricted group to an
   *                            OPEN team. The admin thinks of them as isolated;
   *                            in this team they are not.
   *   direction 'restricted' — adding someone who is already in an open group to a
   *                            RESTRICTED team. The admin thinks they are locking
   *                            the person down; the open group still shows through.
   *
   * Administrators and coordinators are skipped: they see everything regardless, so
   * a warning about group membership would be noise.
   */
  const visibilityWarningsFor = ({ agentIds, teamVisibility, teamName }) => {
    if (!['open', 'restricted'].includes(teamVisibility)) return [];

    return (agentIds || [])
      .map(id => {
        const membership = groupsFor(id);
        if (!membership) return null;
        if (membership.is_administrator || membership.is_coordinator)
          return null;

        if (teamVisibility === 'open') {
          const restricted = membership.restricted || [];
          if (!restricted.length) return null;
          return {
            userId: id,
            direction: 'open',
            teamName,
            groupNames: names(restricted),
          };
        }

        const open = openGroups(membership);
        if (!open.length) return null;
        return {
          userId: id,
          direction: 'restricted',
          teamName,
          groupNames: names(open),
        };
      })
      .filter(Boolean);
  };

  return {
    agentGroups,
    isLoadingAgentGroups,
    fetchAgentGroups,
    groupsFor,
    ledGroups,
    plainRestrictedGroups,
    openGroups,
    names,
    effectiveVisibility,
    visibilityWarningsFor,
  };
}

export default useKaptionGroupVisibility;
